import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin } from 'lucide-react';

interface Props {
  cityName?: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  className?: string;
}

/**
 * Reusable neighborhood dropdown that loads neighborhoods for the selected city
 * by name. Value is the neighborhood name. Disabled if no city is selected or no
 * neighborhoods exist for the city.
 */
export function NeighborhoodFilterSelect({ cityName, value, onChange, className }: Props) {
  const { data: neighborhoods = [] } = useQuery({
    queryKey: ['neighborhoods-by-city-name', cityName],
    enabled: !!cityName,
    queryFn: async () => {
      const { data: city } = await supabase
        .from('cities')
        .select('id')
        .ilike('name', cityName!)
        .maybeSingle();
      if (!city) return [];
      const { data } = await supabase
        .from('neighborhoods')
        .select('id, name, slug')
        .eq('is_active', true)
        .eq('city_id', city.id)
        .order('display_order')
        .order('name');
      return data || [];
    },
  });

  const disabled = !cityName || neighborhoods.length === 0;

  return (
    <Select
      value={value || 'all'}
      onValueChange={v => onChange(v === 'all' ? undefined : v)}
      disabled={disabled}
    >
      <SelectTrigger className={className || 'h-9 w-auto min-w-[140px] text-xs shrink-0 gap-1.5'}>
        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
        <SelectValue
          placeholder={!cityName ? 'Select city first' : neighborhoods.length === 0 ? 'No neighborhoods' : 'All Neighborhoods'}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Neighborhoods</SelectItem>
        {neighborhoods.map((n: any) => (
          <SelectItem key={n.id} value={n.name} className="text-xs">{n.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
